import { Button, InlineNotice } from "@bafe/ui";
import { checkAccountPassword } from "@bafe/validation";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { AuthShell } from "../../components/AuthShell";
import { ErrorPanel } from "../../components/ErrorPanel";
import { PasswordField } from "../../components/PasswordField";
import { api } from "../../lib/api";

export function ResetPassword() {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	const [password, setPassword] = useState("");
	const [touched, setTouched] = useState(false);

	const check = checkAccountPassword(password);
	const passwordError = touched && !check.ok ? (check.message ?? null) : null;

	const confirm = useMutation({
		mutationFn: () => api.confirmPasswordReset({ token: token ?? "", password }),
	});

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setTouched(true);
		if (!check.ok) return;
		confirm.mutate();
	}

	if (token === null) {
		return (
			<AuthShell
				title="This reset link is incomplete"
				footer={
					<Link
						to="/forgot-password"
						className="font-medium text-(--color-brand-700) underline underline-offset-2"
					>
						Request a new link
					</Link>
				}
			>
				<p className="text-sm text-(--text-secondary)">
					Open the link from your email exactly as it was sent, or request a new one.
				</p>
			</AuthShell>
		);
	}

	if (confirm.isSuccess) {
		return (
			<AuthShell title="Your password is set">
				<div className="flex flex-col gap-4">
					<InlineNotice tone="positive">
						Signing in on your other devices will need the new password.
					</InlineNotice>
					<Button variant="primary" fullWidth onClick={() => window.location.assign(`${import.meta.env.BASE_URL.replace(/\/$/, "")}/signin`)}>
						Sign in
					</Button>
				</div>
			</AuthShell>
		);
	}

	return (
		<AuthShell title="Choose a new password">
			<form className="flex flex-col gap-4" onSubmit={submit} noValidate>
				<PasswordField
					label="New password"
					value={password}
					onChange={(value) => {
						setPassword(value);
						setTouched(true);
					}}
					error={passwordError}
					help="At least 10 characters. Longer is better than complicated."
					required
				/>

				{confirm.isError && <ErrorPanel error={confirm.error} title="We could not set that password." />}

				<Button type="submit" variant="primary" fullWidth pending={confirm.isPending} pendingLabel="Saving…">
					Set password
				</Button>
			</form>
		</AuthShell>
	);
}
